import { useEffect, useState } from "react";
import { ChevronRight } from "lucide-react";
import api from "../../lib/api";
import { architectureError, groupModules, kindLabel, moduleLabel, tileCounts } from "../../lib/architecture";
import { ConfidenceBadge, KindBadge, SourceRef } from "./ArchitectureBits";
import { Button } from "../ui/button";
import { Skeleton } from "../ui/skeleton";

export function LoadError({ message, onRetry }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700" role="alert" data-testid="arch-error">
      <p>{message}</p>
      {onRetry ? <Button variant="outline" size="sm" className="mt-3" onClick={onRetry}>Réessayer</Button> : null}
    </div>
  );
}

// A result row: the element, what it is, where it is in the code. The whole row opens its detail.
export function ResultRow({ item, onOpen }) {
  return (
    <li>
      <button type="button" onClick={() => onOpen(item.id)} data-testid="arch-result" data-id={item.id}
        className="flex w-full min-w-0 flex-col gap-1 px-3 py-2.5 text-left hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-jamora [@media(pointer:coarse)]:min-h-11">
        <span className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
          <span className="min-w-0 break-all text-sm font-medium text-slate-900">{item.name}</span>
          <KindBadge kind={item.kind} />
          {item.confidence && item.confidence !== "static" ? <ConfidenceBadge value={item.confidence} /> : null}
        </span>
        <span className="break-all text-xs text-slate-500">{item.path ? `${item.path}${item.line ? `:${item.line}` : ""}` : moduleLabel(item.module_id)}</span>
      </button>
    </li>
  );
}

export function ResultList({ state, title, onOpen, onMore, hint }) {
  if (state.phase === "loading") return <div className="space-y-2" data-testid="arch-loading">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-12 w-full" />)}</div>;
  if (state.phase === "error") return <LoadError message={state.message} onRetry={state.retry} />;
  const items = state.items || [];
  return (
    <section data-testid="arch-results">
      <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
        <span className="text-xs text-slate-500" data-testid="arch-count">{state.total} résultat{state.total > 1 ? "s" : ""}{state.truncated ? ` — les ${items.length} premiers, précisez la recherche` : ""}</span>
      </div>
      {items.length ? (
        <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200 bg-white">{items.map((item) => <ResultRow key={item.id} item={item} onOpen={onOpen} />)}</ul>
      ) : (
        <div className="rounded-lg border border-dashed border-slate-300 p-6 text-center text-sm text-slate-600" data-testid="arch-empty">
          Aucun élément ne correspond.{hint ? ` ${hint}` : ""}
        </div>
      )}
      {onMore && items.length < state.total && !state.truncated ? <Button variant="outline" size="sm" className="mt-3" onClick={onMore} data-testid="arch-more">Voir la suite ({state.total - items.length})</Button> : null}
    </section>
  );
}

export function DomainsTab({ overview, onPick }) {
  const groups = groupModules(overview.modules);
  return (
    <div className="space-y-5" data-testid="arch-domains">
      {groups.map(([group, modules]) => (
        <section key={group}>
          <h3 className="mb-2 text-2xs font-semibold uppercase tracking-wider text-slate-500">{group} <span className="font-normal">({modules.length})</span></h3>
          <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
            {modules.map((m) => (
              <button key={m.id} type="button" onClick={() => onPick(m.id)} data-testid="arch-domain" data-id={m.id}
                className="flex min-w-0 items-start justify-between gap-2 rounded-lg border border-slate-200 bg-white p-3 text-left hover:border-slate-300 hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-jamora">
                <span className="min-w-0">
                  <span className="block break-all text-sm font-semibold text-slate-900">{moduleLabel(m.id)}</span>
                  <span className="mt-1 block text-xs text-slate-600">
                    {tileCounts(m).length ? tileCounts(m).map(([kind, count]) => `${count} ${kindLabel(kind).toLowerCase()}${count > 1 && !/s$/.test(kindLabel(kind)) ? "s" : ""}`).join(" · ") : `${m.total} élément${m.total > 1 ? "s" : ""}`}
                  </span>
                </span>
                <ChevronRight size={16} className="mt-0.5 shrink-0 text-slate-400" aria-hidden />
              </button>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

export function UnknownsTab({ snapshot }) {
  const [kind, setKind] = useState("");
  const [text, setText] = useState("");
  const [state, setState] = useState({ phase: "loading", items: [], kinds: {}, total: 0 });
  const [attempt, setAttempt] = useState(0);
  const base = snapshot ? { snapshot } : {};
  useEffect(() => {
    let live = true;
    setState((s) => ({ ...s, phase: "loading" }));
    api.get("/architecture/unknowns", { params: { ...base, ...(kind ? { kind } : {}), ...(text.trim() ? { q: text.trim() } : {}), limit: 50 } })
      .then(({ data }) => live && setState({ phase: "ready", ...data }))
      .catch((e) => live && setState((s) => ({ ...s, phase: "error", message: architectureError(e, "Impossible de charger la liste. Réessayez dans quelques instants.") })));
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, text, snapshot, attempt]);
  const more = () => api.get("/architecture/unknowns", { params: { ...base, ...(kind ? { kind } : {}), ...(text.trim() ? { q: text.trim() } : {}), skip: state.items.length, limit: 50 } })
    .then(({ data }) => setState((s) => ({ ...s, ...data, items: [...s.items, ...data.items] }))).catch(() => {});
  return (
    <div data-testid="arch-unknowns" className="space-y-3">
      <p className="text-sm text-slate-600">Ce que l'analyse n'a pas pu trancher : jamais supprimé, jamais deviné. Chaque ligne dit où regarder.</p>
      <div className="flex flex-wrap items-center gap-2">
        <select aria-label="Nature" value={kind} onChange={(e) => setKind(e.target.value)} data-testid="unknown-kind"
          className="h-10 rounded-md border border-slate-300 bg-white px-2 text-sm text-slate-900 [@media(pointer:coarse)]:h-11">
          <option value="">Toutes les natures ({Object.values(state.kinds).reduce((a, b) => a + b, 0)})</option>
          {Object.entries(state.kinds).map(([k, n]) => (<option key={k} value={k}>{k.replace(/_/g, " ")} ({n})</option>))}
        </select>
        <input aria-label="Filtrer par fichier ou détail" value={text} onChange={(e) => setText(e.target.value)} placeholder="Filtrer par fichier ou détail" data-testid="unknown-text"
          className="h-10 min-w-0 flex-1 rounded-md border border-slate-300 bg-white px-3 text-base text-slate-900 sm:text-sm [@media(pointer:coarse)]:h-11 sm:max-w-xs" />
      </div>
      {state.phase === "error" ? <LoadError message={state.message} onRetry={() => setAttempt((n) => n + 1)} /> : null}
      {state.phase === "loading" && !state.items.length ? <Skeleton className="h-24 w-full" /> : null}
      {state.phase !== "error" && state.phase !== "loading" && !state.items.length ? <div className="rounded-lg border border-dashed border-slate-300 p-6 text-center text-sm text-slate-600" data-testid="arch-empty">Rien à investiguer pour ce filtre.</div> : null}
      {state.items.length ? (
        <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200 bg-white">
          {state.items.map((u, i) => (
            <li key={`${u.kind}-${u.path}-${u.line}-${i}`} className="px-3 py-2.5" data-testid="unknown-item">
              <div className="flex flex-wrap items-center gap-2"><span className="rounded bg-slate-100 px-1.5 py-0.5 text-2xs font-medium text-slate-700">{u.kind.replace(/_/g, " ")}</span><SourceRef path={u.path} line={u.line} /></div>
              <p className="mt-1 break-words text-sm text-slate-700">{u.detail}</p>
            </li>
          ))}
        </ul>
      ) : null}
      {state.items.length < state.total ? <Button variant="outline" size="sm" onClick={more} data-testid="arch-more">Voir la suite ({state.total - state.items.length})</Button> : null}
    </div>
  );
}

const Table = ({ rows, head }) => (
  <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
    <table className="w-full text-sm">
      {head ? <thead className="bg-slate-50 text-left text-xs text-slate-600"><tr>{head.map((h) => <th key={h} className="px-3 py-2 font-medium">{h}</th>)}</tr></thead> : null}
      <tbody className="divide-y divide-slate-100">{rows.map((r, i) => (<tr key={i}>{r.map((c, j) => <td key={j} className={`px-3 py-2 ${j === 0 ? "text-slate-700" : "text-slate-900"} break-words`}>{c}</td>)}</tr>))}</tbody>
    </table>
  </div>
);

const FRIENDLY = {
  calls_resolved: "Appels de fonction résolus", calls_unresolved: "Appels non résolus (méthodes d'objets, bibliothèques, dynamique)", endpoints_total: "Routes d'API",
  endpoints_with_a_test: "Routes citées par au moins un test", endpoints_without_resolved_data: "Routes sans accès à une collection résolu", events_declared: "Événements de notification",
  emits_resolved: "Émissions d'événement résolues", emits_unresolved: "Émissions d'événement non résolues", ui_pages_traced: "Pages d'interface tracées jusqu'aux routes",
  ui_calls_matched: "Appels d'interface rapprochés d'une route", ui_calls_variable_base: "Appels d'interface à adresse variable", state_tables: "Tables d'états",
  cycles_python_strict: "Cycles d'imports Python (imports directs)", cycles_python_without_hub: "Cycles d'imports Python hors server.py",
};

export function CoverageTab({ overview }) {
  const c = overview.coverage || {};
  const relations = c.relations || {};
  return (
    <div className="space-y-5" data-testid="arch-coverage">
      <section>
        <h3 className="mb-2 text-sm font-semibold text-slate-900">Fichiers du dépôt</h3>
        <Table head={["Mesure", "Valeur"]} rows={[
          ["Fichiers suivis par Git", c.files_discovered?.toLocaleString("fr-FR")],
          ["Fichiers comptabilisés", `${c.files_accounted_for?.toLocaleString("fr-FR")} / ${c.files_discovered?.toLocaleString("fr-FR")}`],
          ["Par statut", Object.entries(c.files_by_status || {}).map(([k, v]) => `${k} ${v}`).join(" · ")],
          ["Python analysé", c.python ? `${c.python.parsed} / ${c.python.discovered} (erreurs : ${c.python.errors})` : "—"],
          ["JavaScript analysé", c.javascript ? `${c.javascript.parsed} / ${c.javascript.discovered} (erreurs : ${c.javascript.errors})` : "—"],
        ]} />
      </section>
      <section>
        <h3 className="mb-2 text-sm font-semibold text-slate-900">Relations : ce qui est résolu, et ce qui ne l'est pas</h3>
        <Table head={["Mesure", "Valeur"]} rows={Object.entries(FRIENDLY).filter(([k]) => relations[k] !== undefined).map(([k, label]) => [label, Number(relations[k]).toLocaleString("fr-FR")])} />
        <p className="mt-1 text-xs text-slate-500">Un appel non résolu ne veut pas dire qu'il n'existe pas : une écriture faite par une méthode d'objet n'apparaît pas comme relation.</p>
      </section>
      <section>
        <h3 className="mb-2 text-sm font-semibold text-slate-900">Fichiers exclus, avec leur motif</h3>
        <Table head={["Catégorie", "Fichiers", "Motif"]} rows={(overview.exclusions || []).map((e) => [e.category, e.files, e.reason])} />
      </section>
    </div>
  );
}
